import { useMemo, useState } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconFileCode,
  IconFileTypePdf,
  IconSearch,
  IconSelector,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { DatePickerInput, Group, IconAction, Input, Loader, Paper, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { toastError } from "@/lib/toast";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import {
  descargarAcusePdf,
  descargarXmlDeFactura,
  type Factura,
} from "../services/facturas.api";
import {
  filtrarFacturas,
  ordenarFacturas,
  siguienteSort,
  type FacturaFiltros,
  type FacturaSort,
  type FacturaSortKey,
} from "../utils/ordenar-facturas";
import { EstatusFacturaBadge } from "./EstatusFacturaBadge";

const MXN = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2 });

function fecha(iso: string | null): string {
  return iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Columnas · el orden y la cantidad deben coincidir EXACTAMENTE con las celdas
 * que renderiza `FacturaRow`. `key` presente → columna ordenable + filtrable
 * (fila de búsqueda bajo los encabezados · mismo patrón que Mis solicitudes);
 * `key: null` → columna informativa sin orden/filtro (Acciones).
 * Columnas de fecha filtran por DÍA con un selector de fecha.
 */
const COLS: {
  key: FacturaSortKey | null;
  label: string;
  align?: "right" | "center";
  w: number;
  filtro?: "fecha";
}[] = [
  { key: "uuid", label: "UUID", w: 280 },
  { key: "emisor", label: "Emisor", w: 200 },
  { key: "receptor", label: "Receptor", w: 200 },
  { key: "validacion", label: "Fecha validación", w: 140, filtro: "fecha" },
  { key: "timbrado", label: "Fecha timbrado", w: 140, filtro: "fecha" },
  { key: "emision", label: "Fecha emisión", w: 140, filtro: "fecha" },
  { key: "total", label: "Total", align: "right", w: 110 },
  { key: "moneda", label: "Moneda", w: 90 },
  { key: "estatus", label: "Estatus", w: 120 },
  { key: null, label: "Acciones", align: "center", w: 90 },
  { key: "serie", label: "Serie", w: 80 },
  { key: "folio", label: "Folio", w: 80 },
  { key: "ordenCompra", label: "Orden de compra", w: 130 },
  { key: "tipo", label: "Tipo", w: 70 },
  { key: "observaciones", label: "Observaciones", w: 260 },
  { key: "numDoc", label: "Núm. documento", w: 120 },
];

/**
 * Tabla ancha de "Mis facturas" · orden por columna (click en el encabezado)
 * y filtro de texto/fecha por columna (fila de búsqueda bajo los encabezados) ·
 * mismo patrón que la tabla de Mis solicitudes. Filtros y orden viven en el
 * padre y van al SERVER (paginado server-side); aquí además se filtra/ordena
 * en cliente la página visible para feedback instantáneo durante el debounce.
 */
export function FacturasTable({
  facturas,
  filtros,
  sort,
  buscando = false,
  onFiltrar,
  onOrdenar,
}: {
  facturas: Factura[];
  filtros: FacturaFiltros;
  sort: FacturaSort | null;
  buscando?: boolean;
  onFiltrar: (key: FacturaSortKey, valor: string) => void;
  onOrdenar: (sort: FacturaSort) => void;
}) {
  const visibles = useMemo(
    () => ordenarFacturas(filtrarFacturas(facturas, filtros), sort),
    [facturas, filtros, sort],
  );

  const alOrdenar = (key: FacturaSortKey) => onOrdenar(siguienteSort(sort, key));

  return (
    <Paper className="relative">
      {buscando && (
        <Group gap={6} className="absolute top-2 right-3 z-10 pointer-events-none">
          <Loader size="xs" />
          <Text size="xs" c="dimmed" fw={500}>
            Buscando…
          </Text>
        </Group>
      )}
      <div className="overflow-x-auto">
        <Table
          highlightOnHover
          verticalSpacing="sm"
          horizontalSpacing="xs"
          withRowBorders
          minWidth={2050}
          className="uppercase"
        >
          <Thead>
            <Tr>
              {COLS.map((c, i) =>
                c.key ? (
                  <ThOrdenable
                    key={c.key}
                    sortKey={c.key}
                    sort={sort}
                    onSort={alOrdenar}
                    align={c.align === "right" ? "right" : "left"}
                    w={c.w}
                  >
                    {c.label}
                  </ThOrdenable>
                ) : (
                  <Th key={`x-${i}`} align={c.align} style={{ width: c.w, verticalAlign: "top" }}>
                    <Text size="sm" fw={600} className="whitespace-nowrap">
                      {c.label}
                    </Text>
                  </Th>
                ),
              )}
            </Tr>
            <Tr>
              {COLS.map((c, i) => {
                if (!c.key) return <Th key={`xf-${i}`} className="p-1" />;
                const key = c.key;
                return (
                  <Th key={key} className="p-1">
                    {c.filtro === "fecha" ? (
                      <DatePickerInput
                        size="xs"
                        clearable
                        placeholder="Fecha…"
                        value={parseIso(filtros[key])}
                        onChange={(d) => onFiltrar(key, formatIso(d))}
                        className="normal-case"
                      />
                    ) : (
                      <Input
                        size="xs"
                        placeholder="Buscar…"
                        leftSection={<IconSearch size={12} stroke={1.6} />}
                        value={filtros[key] ?? ""}
                        onChange={(e) => onFiltrar(key, e.currentTarget.value)}
                        aria-label={`Filtrar por ${c.label}`}
                        className="normal-case whitespace-nowrap overflow-hidden text-ellipsis"
                      />
                    )}
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody style={{ opacity: buscando ? 0.5 : 1, transition: "opacity 120ms ease" }}>
            {visibles.length === 0 ? (
              <Tr>
                <Td colSpan={COLS.length}>
                  <Text size="sm" c="dimmed" ta="center" className="py-3 normal-case">
                    Sin resultados para los filtros.
                  </Text>
                </Td>
              </Tr>
            ) : (
              visibles.map((f) => <FacturaRow key={f.id} f={f} />)
            )}
          </Tbody>
        </Table>
      </div>
    </Paper>
  );
}

/** Encabezado clickeable con indicador de orden (mismo patrón que Mis solicitudes). */
function ThOrdenable({
  children,
  sortKey,
  sort,
  onSort,
  align = "left",
  w,
}: {
  children: React.ReactNode;
  sortKey: FacturaSortKey;
  sort: FacturaSort | null;
  onSort: (key: FacturaSortKey) => void;
  align?: "left" | "right";
  w?: number;
}) {
  const activa = sort?.key === sortKey;
  const Icono = activa ? (sort!.dir === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Th className="p-0" style={{ width: w, verticalAlign: "top" }}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="w-full cursor-pointer px-2.5 py-2 text-left"
        aria-label={`Ordenar por ${typeof children === "string" ? children : sortKey}`}
      >
        <Group gap={2} wrap="nowrap" align="flex-start" justify={align === "right" ? "flex-end" : "flex-start"}>
          <Text size="sm" fw={600} className="whitespace-nowrap uppercase" style={{ lineHeight: 1.2 }}>
            {children}
          </Text>
          <Icono
            size={14}
            stroke={1.8}
            className="shrink-0"
            style={{ marginTop: 2 }}
            color={activa ? "var(--color-multiva-600)" : undefined}
          />
        </Group>
      </button>
    </Th>
  );
}

function FacturaRow({ f }: { f: Factura }) {
  const [descargando, setDescargando] = useState<"xml" | "pdf" | null>(null);

  const descargar = async (tipo: "xml" | "pdf") => {
    setDescargando(tipo);
    try {
      await (tipo === "xml" ? descargarXmlDeFactura(f) : descargarAcusePdf(f));
    } catch {
      toastError({
        title: "No se pudo descargar",
        message:
          tipo === "xml"
            ? "El XML original ya no está disponible."
            : "No se pudo generar el PDF del acuse.",
      });
    } finally {
      setDescargando(null);
    }
  };

  return (
    <Tr>
      <Td>
        {/* El UUID fiscal se muestra en MAYÚSCULAS (convención del SAT). */}
        <Text size="xs" ff="mono" className="uppercase whitespace-nowrap">
          {f.uuid}
        </Text>
      </Td>
      <Td>
        <Text size="sm" truncate title={`${f.nombreEmisor} · ${f.rfcEmisor}`}>
          {f.nombreEmisor}
        </Text>
        <Text size="xs" c="dimmed">
          {f.rfcEmisor}
        </Text>
      </Td>
      <Td>
        <Text size="sm" truncate title={`${f.nombreReceptor} · ${f.rfcReceptor}`}>
          {f.nombreReceptor}
        </Text>
        <Text size="xs" c="dimmed">
          {f.rfcReceptor}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{fecha(f.fechaValidacion)}</Text>
      </Td>
      <Td>
        <Text size="sm">{fecha(f.fechaTimbrado)}</Text>
      </Td>
      <Td>
        <Text size="sm">{fecha(f.fechaEmision)}</Text>
      </Td>
      <Td align="right">
        <Text size="sm" fw={500}>
          {MXN.format(f.total)}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{f.moneda}</Text>
      </Td>
      <Td>
        <EstatusFacturaBadge estatus={f.estatus} />
      </Td>
      <Td>
        <Group gap={4} justify="center" wrap="nowrap">
          <IconAction
            label={`Descargar XML de ${f.uuid}`}
            loading={descargando === "xml"}
            onClick={() => descargar("xml")}
          >
            <IconFileCode size={18} />
          </IconAction>
          <IconAction
            label={`Descargar acuse PDF de ${f.uuid}`}
            iconColor="peligro-600"
            loading={descargando === "pdf"}
            onClick={() => descargar("pdf")}
          >
            <IconFileTypePdf size={18} />
          </IconAction>
        </Group>
      </Td>
      <Td>
        <Text size="sm">{f.serie ?? "—"}</Text>
      </Td>
      <Td>
        <Text size="sm">{f.folio ?? "—"}</Text>
      </Td>
      <Td>
        <Text size="sm">{f.ordenCompraClave ?? "—"}</Text>
      </Td>
      <Td>
        <Text size="sm">{f.tipoComprobante}</Text>
      </Td>
      <Td>
        <Text size="xs" c="dimmed" truncate className="normal-case" title={f.observaciones ?? undefined}>
          {f.observaciones ?? "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm" title="Número de la solicitud de pago">
          {f.numeroSolicitud}
        </Text>
      </Td>
    </Tr>
  );
}
